// src/pages/Cart.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./styles/cart.css";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BsCartPlusFill } from "react-icons/bs";
import { IoArrowBack, IoClose } from "react-icons/io5";
import { FiPhoneCall } from "react-icons/fi";

import {
  getUserCart,
  deleteCartProduct,
  updateCartProduct,
  deleteUserCart,
  // ✅ used only for migrating guest cart -> user cart
  addProdToCart,
} from "../app/features/user/userSlice";

// ✅ Print prices list (needed to recompute print unit + setup fee in cart)
import { getPprices } from "../app/features/printPrice/printPriceSlice";

import product1 from "../images/products/stamp.jpg";

/**
 * ✅ Size display logic (clean label)
 */
const stripMeasurements = (val) => {
  const s = String(val || "").trim();
  if (!s) return "";

  let out = s.replace(/\s*\([^)]*\b(mm|cm|in|inch|inches)\b[^)]*\)\s*$/i, "");
  out = out.replace(/\s*\d+\s*(?:×|x)\s*\d+\s*(?:mm|cm|in|inch|inches)\s*$/i, "");
  out = out.replace(/\s*\b(mm|cm|in|inch|inches)\b\s*$/i, "");
  out = out.replace(/\s+/g, " ").trim();

  return out;
};

const getSizeLabel = (sizeValue) => {
  if (!sizeValue) return "";
  if (typeof sizeValue === "object") {
    const pick = sizeValue?.title ?? sizeValue?.name ?? sizeValue?.label ?? sizeValue?.size ?? "";
    return stripMeasurements(pick);
  }
  return stripMeasurements(sizeValue);
};

const formatUGX = (n) => `UGX ${Number(n || 0).toLocaleString()}`;

/**
 * ✅ slug-aware product path
 */
const getProductPath = (slugOrId) => `/product/${encodeURIComponent(String(slugOrId || ""))}`;

/**
 * ✅ SAME BULK DISCOUNT LOGIC as SingleProduct (qty-based)
 */
const getSizePriceByQtyDisplay = (sizeObj, qty = 1, basePrice = 0) => {
  const base = Number(basePrice || 0);
  const q = Math.max(1, Number(qty || 1));

  if (!sizeObj || typeof sizeObj !== "object") {
    return { current: base, old: null, source: "base", isBulkDiscount: false };
  }

  const price = sizeObj?.price;
  const discountPrice = sizeObj?.discountPrice;
  const discountMinQty = sizeObj?.discountMinQty;
  const adj = sizeObj?.priceAdjustment;

  const priceNum = Number(price);
  const hasPrice =
    price !== null &&
    price !== undefined &&
    String(price).trim() !== "" &&
    Number.isFinite(priceNum) &&
    priceNum > 0;

  const hasDiscount =
    discountPrice !== null &&
    discountPrice !== undefined &&
    String(discountPrice).trim() !== "" &&
    Number.isFinite(Number(discountPrice));

  const hasMinQty =
    discountMinQty !== null &&
    discountMinQty !== undefined &&
    String(discountMinQty).trim() !== "" &&
    Number.isFinite(Number(discountMinQty));

  const baseCurrent = hasPrice ? Number(price) : base + Number(adj || 0);

  const bulkApplies = hasDiscount && hasMinQty && q >= Number(discountMinQty);
  if (bulkApplies) {
    const bulk = Number(discountPrice);
    const old = baseCurrent > bulk ? baseCurrent : null;
    return {
      current: bulk,
      old,
      source: "size.bulkDiscount",
      isBulkDiscount: true,
    };
  }

  return {
    current: baseCurrent,
    old: null,
    source: hasPrice ? "size.price" : "size.adjustment",
    isBulkDiscount: false,
  };
};

/**
 * ✅ PRODUCT BULK DISCOUNT (optional)
 */
const getProductPriceByQtyDisplay = (qty = 1, baseCurrent = 0, baseOld = null, product = null) => {
  const q = Math.max(1, Number(qty || 1));

  const minRaw =
    product?.bulkDiscount?.minQty ??
    product?.bulkDiscountMinQty ??
    product?.bulkMinQty ??
    product?.bulkDiscountQty ??
    null;

  const priceRaw =
    product?.bulkDiscount?.price ??
    product?.bulkDiscountPrice ??
    product?.bulkPrice ??
    product?.bulkDiscountUnitPrice ??
    null;

  const min = minRaw !== "" && minRaw !== null && minRaw !== undefined ? Number(minRaw) : null;
  const disc = priceRaw !== "" && priceRaw !== null && priceRaw !== undefined ? Number(priceRaw) : null;

  const hasBulk = Number.isFinite(min) && min >= 1 && Number.isFinite(disc) && disc >= 0;

  if (hasBulk && q >= min) {
    const old = Number(baseCurrent) > disc ? Number(baseCurrent) : null;
    return {
      current: disc,
      old: old || baseOld || null,
      source: "product.bulkDiscount",
      isBulkDiscount: true,
      bulkMinQty: min,
    };
  }

  return {
    current: baseCurrent,
    old: baseOld || null,
    source: "product.base",
    isBulkDiscount: false,
    bulkMinQty: hasBulk ? min : null,
  };
};

/**
 * ✅ COLOR PRICE LOGIC
 */
const getColorPriceByQtyDisplay = (product, colorId, qty = 1) => {
  const q = Math.max(1, Number(qty || 1));
  const p = product || {};

  const basePrice = Number(p?.price ?? 0);

  const productDiscountedPrice =
    p?.discountedPrice !== null &&
    p?.discountedPrice !== undefined &&
    p?.discountedPrice !== ""
      ? Number(p.discountedPrice)
      : null;

  const productDiscountMinQty =
    p?.discountMinQty !== null &&
    p?.discountMinQty !== undefined &&
    p?.discountMinQty !== ""
      ? Number(p.discountMinQty)
      : null;

  const productBulkMinQty =
    p?.bulkDiscount?.minQty !== null &&
    p?.bulkDiscount?.minQty !== undefined &&
    p?.bulkDiscount?.minQty !== ""
      ? Number(p.bulkDiscount.minQty)
      : null;

  const productBulkPrice =
    p?.bulkDiscount?.price !== null &&
    p?.bulkDiscount?.price !== undefined &&
    p?.bulkDiscount?.price !== ""
      ? Number(p.bulkDiscount.price)
      : null;

  const variants = Array.isArray(p?.colorVariants) ? p.colorVariants : [];

  const variant =
    colorId != null
      ? variants.find((v) => String(v?.color?._id || v?.color) === String(colorId))
      : null;

  const finalBasePrice =
    variant?.price !== null &&
    variant?.price !== undefined &&
    variant?.price !== ""
      ? Number(variant.price)
      : basePrice;

  const finalDiscountedPrice =
    variant?.discountedPrice !== null &&
    variant?.discountedPrice !== undefined &&
    variant?.discountedPrice !== ""
      ? Number(variant.discountedPrice)
      : productDiscountedPrice !== null && productDiscountedPrice < finalBasePrice
      ? productDiscountedPrice
      : null;

  const finalDiscountMinQty =
    finalDiscountedPrice !== null
      ? variant?.discountMinQty !== null &&
        variant?.discountMinQty !== undefined &&
        variant?.discountMinQty !== ""
        ? Number(variant.discountMinQty)
        : productDiscountMinQty
      : null;

  const finalBulkPrice =
    variant?.bulkDiscount?.price !== null &&
    variant?.bulkDiscount?.price !== undefined &&
    variant?.bulkDiscount?.price !== ""
      ? Number(variant.bulkDiscount.price)
      : productBulkPrice !== null && productBulkPrice <= finalBasePrice
      ? productBulkPrice
      : null;

  const finalBulkMinQty =
    finalBulkPrice !== null
      ? variant?.bulkDiscount?.minQty !== null &&
        variant?.bulkDiscount?.minQty !== undefined &&
        variant?.bulkDiscount?.minQty !== ""
        ? Number(variant.bulkDiscount.minQty)
        : productBulkMinQty
      : null;

  if (
    finalBulkMinQty !== null &&
    Number.isFinite(finalBulkMinQty) &&
    finalBulkPrice !== null &&
    Number.isFinite(finalBulkPrice) &&
    q >= finalBulkMinQty
  ) {
    return {
      current: finalBulkPrice,
      old: finalBasePrice > finalBulkPrice ? finalBasePrice : null,
      source: "color.bulkDiscount",
      isBulkDiscount: true,
      discountMinQty: finalBulkMinQty,
      variant,
    };
  }

  if (
    finalDiscountedPrice !== null &&
    Number.isFinite(finalDiscountedPrice) &&
    finalDiscountMinQty !== null &&
    Number.isFinite(finalDiscountMinQty) &&
    q >= finalDiscountMinQty
  ) {
    return {
      current: finalDiscountedPrice,
      old: finalBasePrice > finalDiscountedPrice ? finalBasePrice : null,
      source: "color.discount",
      isBulkDiscount: false,
      discountMinQty: finalDiscountMinQty,
      variant,
    };
  }

  return {
    current: finalBasePrice,
    old: null,
    source: variant?.price != null ? "color.price" : "product.base",
    isBulkDiscount: false,
    discountMinQty: null,
    variant,
  };
};

/* =========================
   ✅ GUEST CART (localStorage)
========================= */
const GUEST_CART_KEY = "kupto_guest_cart_v1";

const readGuestCart = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

const writeGuestCart = (list) => {
  const safe = Array.isArray(list) ? list : [];
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(safe));
  window.dispatchEvent(new Event("guestCartUpdated"));
};

const buildGuestKey = (x) => {
  const pid = String(x?.productId || "");
  const size = x?.size ? String(x.size) : "";
  const color = x?.color ? String(x.color) : "";
  const side = x?.printSide ? String(x.printSide) : "";
  return `${pid}__${size}__${color}__${side}`;
};

/* =========================
   ✅ IMAGE HELPERS
========================= */
const normalizeImg = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  return val?.url || "";
};

const getFirstProductImage = (p) => {
  if (!p) return "";
  if (Array.isArray(p?.images) && p.images.length) {
    return normalizeImg(p.images[0]);
  }
  return normalizeImg(p?.image);
};

const getVariantImageByColor = (productObj, colorId) => {
  if (!productObj || !colorId) return "";
  const list = Array.isArray(productObj?.variantImages) ? productObj.variantImages : [];
  const hit = list.find((v) => String(v?.color?._id || v?.color || "") === String(colorId));
  return hit?.images?.[0]?.url || "";
};

const pickCartItemImage = (cartItem, productObj) => {
  const fromCart = normalizeImg(cartItem?.variantImage);
  if (fromCart) return fromCart;

  const colorId = cartItem?.color?._id || cartItem?.color || "";
  const fromVariantMap = getVariantImageByColor(productObj, colorId);
  if (fromVariantMap) return fromVariantMap;

  const fromProd = getFirstProductImage(productObj);
  return fromProd || product1;
};

/* =========================
   ✅ SKELETON (desktop + app)
   ✅ UPDATED: mobile checkout bar skeleton moved INSIDE mobile list
========================= */
const CartSkeleton = () => {
  return (
    <div className="ct-shell">
      {/* DESKTOP TABLE SKELETON */}
      <div className="ct-card ct-desktopOnly">
        <div className="ct-head">
          <div className="ct-col ct-col--product">Product</div>
          <div className="ct-col ct-col--price">Price</div>
          <div className="ct-col ct-col--qty">Quantity</div>
          <div className="ct-col ct-col--sub">Subtotal</div>
        </div>

        <div className="ct-body">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="ct-row" key={`sk-d-${i}`}>
              <div className="ct-col ct-col--product">
                <div className="ct-prod">
                  <div className="ct-img">
                    <div className="sk sk-img" />
                  </div>

                  <div className="ct-prodText" style={{ width: "100%" }}>
                    <div className="sk sk-line" style={{ width: "58%", height: 14 }} />
                    <div className="sk sk-line" style={{ width: "38%", height: 12, marginTop: 8 }} />
                    <div className="sk sk-line" style={{ width: "28%", height: 12, marginTop: 6 }} />
                  </div>
                </div>
              </div>

              <div className="ct-col ct-col--price">
                <div className="sk sk-line" style={{ width: 96, height: 14 }} />
                <div className="sk sk-line" style={{ width: 76, height: 12, marginTop: 8 }} />
              </div>

              <div className="ct-col ct-col--qty">
                <div className="ct-qty ct-qty--sk">
                  <div className="sk sk-qtyBtn" />
                  <div className="sk sk-qtyMid" />
                  <div className="sk sk-qtyBtn" />
                </div>
              </div>

              <div className="ct-col ct-col--sub">
                <div className="sk sk-line" style={{ width: 104, height: 14 }} />
              </div>
            </div>
          ))}
        </div>

        <div className="ct-bottom">
          <div className="sk sk-line" style={{ width: 170, height: 14 }} />
          <div className="sk sk-pill" style={{ width: 150, height: 14 }} />
        </div>
      </div>

      {/* DESKTOP SUMMARY SKELETON */}
      <aside className="ct-summary ct-desktopOnly">
        <div className="sk sk-line" style={{ width: 160, height: 14, marginBottom: 14 }} />

        <div className="ct-sRows">
          {Array.from({ length: 3 }).map((_, i) => (
            <div className="ct-sRow" key={`sk-s-${i}`}>
              <div className="sk sk-line" style={{ width: 110, height: 12 }} />
              <div className="sk sk-line" style={{ width: 80, height: 12 }} />
            </div>
          ))}
          <div className="ct-sDivider" />
          <div className="ct-sRow ct-sRow--total">
            <div className="sk sk-line" style={{ width: 90, height: 14 }} />
            <div className="sk sk-line" style={{ width: 110, height: 14 }} />
          </div>
        </div>

        <div className="sk sk-btn" style={{ marginTop: 12 }} />
      </aside>

      {/* APP (MOBILE) SKELETON */}
      <div className="ct-mobileOnly">
        <div className="ct-appSummary ct-appSummary--sk">
          <div className="sk sk-line" style={{ width: 130, height: 12, marginBottom: 10 }} />
          <div className="ct-appSumRow">
            <div className="sk sk-line" style={{ width: 90, height: 12 }} />
            <div className="sk sk-line" style={{ width: 120, height: 14 }} />
          </div>
        </div>

        <div className="ct-appList ct-appList--sk">
          {Array.from({ length: 3 }).map((_, i) => (
            <div className="ct-appItem" key={`sk-m-${i}`}>
              <div className="ct-appItemMain">
                <div className="ct-appThumb">
                  <div className="sk sk-img" />
                </div>

                <div className="ct-appInfo">
                  <div className="sk sk-line" style={{ width: "70%", height: 13, marginBottom: 8 }} />
                  <div className="sk sk-line" style={{ width: "52%", height: 12, marginBottom: 10 }} />

                  <div className="ct-appPriceRow">
                    <div className="ct-appPriceBlock">
                      <div className="sk sk-line" style={{ width: 120, height: 14 }} />
                      <div className="ct-appOldRow">
                        <div className="sk sk-line" style={{ width: 90, height: 12 }} />
                        <div className="sk sk-pill" style={{ width: 56, height: 18 }} />
                      </div>
                    </div>

                    <div className="ct-appQtyCol">
                      <div className="ct-appQty ct-appQty--sk">
                        <div className="sk sk-qtyBtn" />
                        <div className="sk sk-qtyMid" />
                        <div className="sk sk-qtyBtn" />
                      </div>
                    </div>
                  </div>

                  <div className="sk sk-line" style={{ width: "46%", height: 12, marginTop: 10 }} />
                </div>
              </div>

              <div className="sk sk-line" style={{ width: 80, height: 12, marginTop: 12 }} />
            </div>
          ))}

          {/* ✅ MOBILE CHECKOUT BAR SKELETON (below items) */}
          <div className="ct-appCheckoutBar ct-appCheckoutBar--sk">
            <div className="ct-appCallBtn">
              <div className="sk sk-circle" style={{ width: 22, height: 22 }} />
            </div>
            <div className="ct-appCheckoutBtn ct-appCheckoutBtn--sk">
              <div className="sk sk-line" style={{ width: 140, height: 14 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Cart = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const returnTo = "/checkout";
  const SUPPORT_PHONE = "+256200949142";

  const authState = useSelector((state) => state?.auth || {});
  const isLoggedIn = Boolean(authState?.user?.token || localStorage.getItem("token"));

  const { cartProduct, isLoading, isError, message } = useSelector((state) => state.auth);

  // ✅ Print prices in store
  const printPriceState = useSelector((state) => state?.printPrice || state?.printPrices || {});
  const printPrices = useMemo(() => {
    const arr = printPriceState?.printPrices;
    return Array.isArray(arr) ? arr : [];
  }, [printPriceState?.printPrices]);

  const [guestCart, setGuestCart] = useState(() => readGuestCart());
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    dispatch(getPprices());
  }, [dispatch]);

  useEffect(() => {
    const onStorage = (e) => {
      if (!e || e.key === GUEST_CART_KEY) setGuestCart(readGuestCart());
    };
    const onEvent = () => setGuestCart(readGuestCart());

    window.addEventListener("storage", onStorage);
    window.addEventListener("guestCartUpdated", onEvent);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("guestCartUpdated", onEvent);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      setShowLoginPrompt(false);
      dispatch(getUserCart());
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (isError && message) toast.error(message);
  }, [isError, message]);

  /* =========================
     ✅ MIGRATE GUEST CART -> USER CART
  ========================== */
  const migratedRef = useRef(false);

  useEffect(() => {
    const run = async () => {
      if (!isLoggedIn) return;
      if (migratedRef.current) return;

      const guest = readGuestCart();
      if (!guest.length) {
        migratedRef.current = true;
        return;
      }

      migratedRef.current = true;

      try {
        for (const it of guest) {
          if (!it?.productId) continue;

          const payload = {
            productId: it.productId,
            quantity: Math.max(1, Number(it.quantity || 1)),
            price: Number(it.price || 0),
          };

          if (it.color) payload.color = it.color;
          if (it.size) payload.size = it.size;

          if (Array.isArray(it.uploadedFiles) && it.uploadedFiles.length) payload.uploadedFiles = it.uploadedFiles;
          if (it.instruction) payload.instruction = String(it.instruction);
          if (it.printSide) payload.printSide = String(it.printSide);

          if (it.printUnitPrice !== undefined) payload.printUnitPrice = Number(it.printUnitPrice || 0);
          if (it.printKey) payload.printKey = String(it.printKey || "");
          if (it.printPricingTitle) payload.printPricingTitle = String(it.printPricingTitle || "");
          if (it.preparePriceOnce !== undefined) payload.preparePriceOnce = Number(it.preparePriceOnce || 0);
          if (it.preparePriceApplied !== undefined) payload.preparePriceApplied = Boolean(it.preparePriceApplied);

          const vImg = it?.variantImage || it?.img || it?.image || "";
          if (vImg) payload.variantImage = vImg;

          // eslint-disable-next-line no-await-in-loop
          await dispatch(addProdToCart(payload)).unwrap();
        }

        writeGuestCart([]);
        setGuestCart([]);
        dispatch(getUserCart());

        toast.success("Guest cart merged into your account cart");
      } catch (err) {
        console.error(err);
        toast.error("Could not merge guest cart. Please try again.");
        migratedRef.current = false;
      }
    };

    run();
  }, [dispatch, isLoggedIn]);

  /* =========================
     ✅ PRINT PRICING RESOLVER
  ========================== */
  const resolvePrintPricing = useCallback(
    (p) => {
      if (!p) return null;

      const directObj =
        p?.printingPrice &&
        typeof p.printingPrice === "object" &&
        (p.printingPrice?.title || p.printingPrice?.printPrice)
          ? p.printingPrice
          : p?.printPricing &&
            typeof p.printPricing === "object" &&
            (p.printPricing?.title || p.printPricing?.printPrice)
          ? p.printPricing
          : p?.printPrice &&
            typeof p.printPrice === "object" &&
            (p.printPrice?.title || p.printPrice?.printPrice)
          ? p.printPrice
          : null;

      const idCandidates = [
        typeof p?.printingPrice === "string" ? p.printingPrice : null,
        typeof p?.printingPriceId === "string" ? p.printingPriceId : null,
        typeof p?.printPricingId === "string" ? p.printPricingId : null,
        typeof p?.printPrice === "string" ? p.printPrice : null,
        typeof p?.printPriceId === "string" ? p.printPriceId : null,
        typeof p?.pprice === "string" ? p.pprice : null,
        typeof p?.ppriceId === "string" ? p.ppriceId : null,
      ].filter(Boolean);

      const titleCandidates = [
        p?.printingType,
        p?.printType,
        p?.printPriceTitle,
        p?.printPricingTitle,
        p?.printPriceName,
        p?.printingPriceTitle,
      ]
        .map((x) => (x == null ? "" : String(x)))
        .map((x) => x.trim())
        .filter(Boolean);

      const byId = (id) => {
        if (!id) return null;
        return (printPrices || []).find((x) => String(x?._id) === String(id)) || null;
      };

      const byTitle = (t) => {
        const s = String(t || "").trim().toLowerCase();
        if (!s) return null;
        return (printPrices || []).find((x) => String(x?.title || "").trim().toLowerCase() === s) || null;
      };

      let record = directObj;

      if (!record) {
        for (const id of idCandidates) {
          record = byId(id);
          if (record) break;
        }
      }

      if (!record) {
        for (const t of titleCandidates) {
          record = byTitle(t);
          if (record) break;
        }
      }

      if (!record) return null;

      const preparePrice = Number(record?.preparePrice ?? 0) || 0;

      const oneSide = Number(record?.printPrice?.oneSide ?? 0) || 0;
      const twoSide = Number(record?.printPrice?.twoSide ?? 0) || 0;

      const discOne = Number(record?.printPriceDiscount?.oneSide ?? 0) || 0;
      const discTwo = Number(record?.printPriceDiscount?.twoSide ?? 0) || 0;

      const discountMinQty =
        record?.discountMinQty !== undefined && record?.discountMinQty !== null
          ? Number(record.discountMinQty)
          : null;

      return {
        id: record?._id || null,
        title: record?.title || "",
        preparePrice,
        discountMinQty: Number.isFinite(discountMinQty) ? discountMinQty : null,
        printPrice: { oneSide, twoSide },
        printPriceDiscount: { oneSide: discOne, twoSide: discTwo },
      };
    },
    [printPrices]
  );

  const getPrintKey = useCallback((pricing) => {
    if (!pricing) return "";
    const recId = pricing?.id ? String(pricing.id) : "";
    const recTitle = pricing?.title ? String(pricing.title) : "";
    return recId ? `pprice:${recId}` : recTitle ? `ppriceTitle:${recTitle.toLowerCase()}` : "";
  }, []);

  /* =========================
     ✅ Backend items
  ========================== */
  const backendItems = useMemo(() => {
    const raw = Array.isArray(cartProduct)
      ? cartProduct
      : Array.isArray(cartProduct?.cart)
      ? cartProduct.cart
      : [];

    return raw
      .map((c) => {
        const cartItemId = c?._id;
        const p = c?.productId;

        if (!cartItemId) return null;

        const productId = (typeof p === "object" ? p?._id : null) || c?.productId;
        const slug =
          (typeof p === "object" ? String(p?.slug || "").trim() : "") ||
          String(c?.slug || "").trim() ||
          String(productId || "");
        const name = (typeof p === "object" ? p?.title || p?.name : null) || "Untitled";
        const qty = Math.max(1, Number(c?.quantity ?? 1) || 1);

        const colorId = c?.color?._id || c?.color || null;

        const colorPrice =
          typeof p === "object"
            ? getColorPriceByQtyDisplay(p, colorId, qty)
            : {
                current: 0,
                old: null,
                source: "product.base",
                isBulkDiscount: false,
                discountMinQty: null,
                variant: null,
              };

        const baseCurrent = Number(colorPrice?.current ?? 0);
        const baseOld = colorPrice?.old ?? null;

        const sizeObj = c?.size && typeof c.size === "object" ? c.size : null;

        const sizePrice = sizeObj ? getSizePriceByQtyDisplay(sizeObj, qty, baseCurrent) : null;

        let unitProduct = sizeObj ? Number(sizePrice?.current ?? baseCurrent) : baseCurrent;
        let oldProduct = sizeObj ? (sizePrice?.old ?? baseOld ?? null) : baseOld;

        if (sizeObj && sizePrice && !sizePrice.isBulkDiscount && sizePrice.source !== "size.price") {
          const prodPrice = getProductPriceByQtyDisplay(qty, sizePrice.current, baseOld, p);
          unitProduct = prodPrice.current;
          oldProduct = prodPrice.old ?? oldProduct;
        } else if (!sizeObj) {
          const prodPrice = getProductPriceByQtyDisplay(qty, baseCurrent, baseOld, p);
          unitProduct = prodPrice.current;
          oldProduct = prodPrice.old ?? oldProduct;
        }

        const pricing = typeof p === "object" ? resolvePrintPricing(p) : null;
        const hasPrintPricing = Boolean(pricing?.title);
        const printSide = String(c?.printSide || "").trim();

        const printMin = Number(pricing?.discountMinQty);
        const printDiscApplies =
          hasPrintPricing && Number.isFinite(printMin) && printMin >= 1 ? qty >= printMin : false;

        const printBase =
          hasPrintPricing && printSide ? Number(pricing?.printPrice?.[printSide] ?? 0) || 0 : 0;
        const printDisc =
          hasPrintPricing && printSide ? Number(pricing?.printPriceDiscount?.[printSide] ?? 0) || 0 : 0;

        const printUnit = printDiscApplies && printDisc > 0 ? printDisc : printBase;

        const unitPrice = Number(unitProduct || 0) + Number(printUnit || 0);
        const oldUnitPrice = oldProduct;

        const color =
          c?.color?.title ||
          c?.color?.name ||
          c?.color?.label ||
          c?.color?.color ||
          (typeof c?.color === "string" ? c.color : "");

        const sizeLabel = getSizeLabel(c?.size);

        const bulkMin =
          p?.bulkDiscount?.minQty ?? p?.bulkDiscountMinQty ?? p?.bulkMinQty ?? p?.bulkDiscountQty ?? null;
        const productBulkMin = bulkMin !== "" && bulkMin !== null && bulkMin !== undefined ? Number(bulkMin) : null;

        const productBulkApplied =
          !sizeObj && Number.isFinite(productBulkMin) && productBulkMin >= 1 && qty >= productBulkMin;

        const img = pickCartItemImage(c, typeof p === "object" ? p : null);

        const printPricingTitle = String(c?.printPricingTitle || pricing?.title || "").trim();
        const printKey = String(c?.printKey || getPrintKey(pricing) || "").trim();
        const preparePriceOnce = Number(c?.preparePriceOnce ?? 0) || 0;
        const preparePriceApplied = Boolean(c?.preparePriceApplied);

        const prepareCandidate = preparePriceOnce > 0 ? preparePriceOnce : Number(pricing?.preparePrice ?? 0) || 0;

        return {
          source: "backend",
          raw: c,

          cartItemId,
          productId,
          slug,
          name,
          color,
          size: sizeLabel,
          qty,
          img,

          unitPrice,
          oldUnitPrice,

          isBulkDiscount: Boolean(
            (sizeObj && sizePrice?.isBulkDiscount) ||
              (!sizeObj && colorPrice?.isBulkDiscount) ||
              productBulkApplied ||
              printDiscApplies
          ),
          discountMinQty:
            (sizeObj && sizePrice?.isBulkDiscount ? sizeObj?.discountMinQty : null) ??
            (!sizeObj && colorPrice?.isBulkDiscount ? colorPrice?.discountMinQty : null) ??
            (productBulkApplied ? productBulkMin : null) ??
            (printDiscApplies ? pricing?.discountMinQty : null),

          printSide,
          hasPrintPricing,
          printUnitPrice: Number(printUnit || 0),
          printPricingTitle,
          printDiscountMinQty: pricing?.discountMinQty ?? null,

          printKey,
          preparePriceOnce: Number(prepareCandidate || 0),
          preparePriceApplied,
        };
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartProduct, resolvePrintPricing, getPrintKey]);

  /* =========================
     ✅ Guest items
  ========================== */
  const guestItems = useMemo(() => {
    const raw = Array.isArray(guestCart) ? guestCart : [];
    return raw
      .map((g) => {
        const productId = g?.productId || g?.product?._id || g?.product;
        if (!productId) return null;

        const key = g?.key || buildGuestKey(g);

        const qty = Math.max(1, Number(g?.quantity ?? g?.qty ?? 1) || 1);
        const unitPrice = Number(g?.price ?? g?.unitPrice ?? 0);

        const oldUnitPrice =
          g?.oldUnitPrice === null || g?.oldUnitPrice === undefined
            ? g?.oldPrice === null || g?.oldPrice === undefined
              ? null
              : Number(g.oldPrice)
            : Number(g.oldUnitPrice);

        const img = g?.variantImage || g?.img || g?.image || g?.images?.[0] || product1;

        const name = g?.name || g?.title || "Untitled";
        const slug = String(g?.slug || g?.productSlug || productId || "").trim();

        const color =
          typeof g?.color === "object"
            ? g?.color?.title || g?.color?.name || g?.color?.label || g?.color?.color || ""
            : g?.colorLabel || g?.color || "";

        const size = g?.sizeLabel || getSizeLabel(g?.size) || (typeof g?.size === "string" ? g.size : "");

        const printSide = String(g?.printSide || "").trim();
        const printKey = String(g?.printKey || "").trim();
        const printPricingTitle = String(g?.printPricingTitle || "").trim();
        const preparePriceOnce = Number(g?.preparePriceOnce ?? 0) || 0;
        const preparePriceApplied = Boolean(g?.preparePriceApplied);

        return {
          source: "guest",
          raw: g,

          cartItemId: key,
          productId: String(productId),
          slug,
          name,
          color,
          size,
          qty,
          img,

          unitPrice,
          oldUnitPrice,

          isBulkDiscount: Boolean(g?.isBulkDiscount),
          discountMinQty: g?.discountMinQty ?? null,

          uploadedFiles: g?.uploadedFiles || [],
          instruction: g?.instruction || "",

          printSide,
          hasPrintPricing: Boolean(printSide || printPricingTitle),
          printUnitPrice: Number(g?.printUnitPrice ?? 0) || 0,
          printPricingTitle,
          printDiscountMinQty: g?.printDiscountMinQty ?? null,

          printKey,
          preparePriceOnce,
          preparePriceApplied,
        };
      })
      .filter(Boolean);
  }, [guestCart]);

  const items = useMemo(() => (isLoggedIn ? backendItems : guestItems), [isLoggedIn, backendItems, guestItems]);
  const showSkeleton = Boolean(isLoggedIn && isLoading);

  /* =========================
     ✅ Setup fee total
  ========================== */
  const setupFeeTotal = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const seen = new Set();
    let sum = 0;

    for (const it of list) {
      const key = String(it?.printKey || "").trim();
      const fee = Number(it?.preparePriceOnce ?? 0) || 0;

      if (!key || fee <= 0) continue;
      if (seen.has(key)) continue;

      seen.add(key);
      sum += fee;
    }

    return Math.max(0, sum);
  }, [items]);

  /* =========================
     ✅ Actions
  ========================== */
  const removeItem = useCallback(
    async (cartItemId) => {
      if (isLoggedIn) {
        try {
          await dispatch(deleteCartProduct(cartItemId)).unwrap();
          dispatch(getUserCart());
          
        } catch (err) {
          toast.error(err?.message || "Failed to remove item");
        }
        return;
      }

      const next = readGuestCart().filter((x) => (x?.key || buildGuestKey(x)) !== String(cartItemId));
      writeGuestCart(next);
      setGuestCart(next);
      toast.success("Removed from cart");
    },
    [dispatch, isLoggedIn]
  );

  const clearCart = useCallback(async () => {
    if (isLoggedIn) {
      try {
        await dispatch(deleteUserCart()).unwrap();
        dispatch(getUserCart());
        toast.success("Cart cleared");
      } catch (err) {
        toast.error(err?.message || "Failed to clear cart");
      }
      return;
    }

    writeGuestCart([]);
    setGuestCart([]);
    toast.success("Cart cleared");
  }, [dispatch, isLoggedIn]);

  const setQty = useCallback(
    async (cartItemId, nextQty) => {
      const qty = Math.max(1, Number(nextQty) || 1);

      if (isLoggedIn) {
        try {
          await dispatch(updateCartProduct({ cartItemId, quantity: qty })).unwrap();
          dispatch(getUserCart());
        } catch (err) {
          toast.error(err?.message || "Failed to update quantity");
        }
        return;
      }

      const list = readGuestCart();
      const next = list.map((x) => {
        const key = x?.key || buildGuestKey(x);
        if (String(key) !== String(cartItemId)) return x;
        return { ...x, key, quantity: qty };
      });
      writeGuestCart(next);
      setGuestCart(next);
    },
    [dispatch, isLoggedIn]
  );

  const incQty = useCallback((cartItemId, currentQty) => setQty(cartItemId, currentQty + 1), [setQty]);
  const decQty = useCallback((cartItemId, currentQty) => setQty(cartItemId, Math.max(1, currentQty - 1)), [setQty]);

  const itemsCount = useMemo(() => items.reduce((a, x) => a + (Number(x.qty) || 0), 0), [items]);

  const itemsSubTotal = useMemo(
    () => items.reduce((a, x) => a + (Number(x.unitPrice) || 0) * (Number(x.qty) || 0), 0),
    [items]
  );

  const total = useMemo(() => Math.max(0, itemsSubTotal + setupFeeTotal), [itemsSubTotal, setupFeeTotal]);

  const openLoginPrompt = useCallback(() => {
    setShowLoginPrompt(true);
  }, []);

  const closeLoginPrompt = useCallback(() => {
    setShowLoginPrompt(false);
  }, []);

  const goToSignIn = useCallback(() => {
    setShowLoginPrompt(false);
    navigate("/identification", {
      state: {
        returnTo,
        from: location.pathname + location.search,
      },
    });
  }, [navigate, location.pathname, location.search]);

  const proceedToCheckout = () => {
    if (!items.length) return;

    if (!isLoggedIn) {
      openLoginPrompt();
      return;
    }

    navigate("/checkout");
  };

  /* =========================
     ✅ RECENTLY VIEWED
  ========================== */
  const [RECENTLY_VIEWED, setRECENTLY_VIEWED] = useState([]);

  const readRecentlyViewed = useCallback(() => {
    const key = "recentlyViewed";
    try {
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      const safe = Array.isArray(list) ? list : [];
      safe.sort((a, b) => (b?.viewedAt || 0) - (a?.viewedAt || 0));
      setRECENTLY_VIEWED(safe);
    } catch {
      setRECENTLY_VIEWED([]);
    }
  }, []);

  useEffect(() => {
    readRecentlyViewed();
    const onStorage = (e) => {
      if (!e) return;
      if (e.key === "recentlyViewed") readRecentlyViewed();
    };
    window.addEventListener("storage", onStorage);

    const onRecentEvent = () => readRecentlyViewed();
    window.addEventListener("recentlyViewedUpdated", onRecentEvent);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("recentlyViewedUpdated", onRecentEvent);
    };
  }, [readRecentlyViewed]);

  const recentTrackRef = useRef(null);
  const scrollRecent = useCallback((dir) => {
    const el = recentTrackRef.current;
    if (!el) return;
    const step = Math.max(260, Math.floor(el.clientWidth * 0.7));
    el.scrollBy({ left: dir === "next" ? step : -step, behavior: "smooth" });
  }, []);

  const goToProduct = (slugOrId) => navigate(getProductPath(slugOrId));

  return (
    <div className="ct-page">
      {/* ✅ MOBILE APP HEADER */}
      <div className="ct-appTop">
        <button type="button" className="ct-appBack" onClick={() => navigate(-1)} aria-label="Back">
          <IoArrowBack />
        </button>
        <div className="ct-appTitle">Cart</div>
        <div className="ct-appRight" />
      </div>

      {/* DESKTOP HERO */}
      <div className="ct-hero">
        <h1 className="ct-title">Shopping Cart</h1>
        <p className="ct-bread">
          Home <span>/</span> Shopping Cart
        </p>

        {!isLoggedIn ? (
          <div style={{ marginTop: 8, color: "#777", fontSize: 13 }}>
            You are viewing a <strong>guest cart</strong>. Login to checkout.
          </div>
        ) : null}
      </div>

      {showSkeleton ? (
        <CartSkeleton />
      ) : (
        <>
          {/* ✅ MOBILE SUMMARY BAR */}
          <div className="ct-appSummary">
            <div className="ct-appSumTitle">CART SUMMARY</div>

            <div className="ct-appSumRow">
              <span>Items</span>
              <strong>{formatUGX(itemsSubTotal)}</strong>
            </div>

            {setupFeeTotal > 0 ? (
              <div className="ct-appSumRow">
                <span>Setup fee</span>
                <strong>{formatUGX(setupFeeTotal)}</strong>
              </div>
            ) : null}

            <div className="ct-appSumRow" style={{ marginTop: 6 }}>
              <span>Total</span>
              <strong>{formatUGX(total)}</strong>
            </div>
          </div>

          <div className="ct-shell">
            {/* =========================
                DESKTOP TABLE CARD
            ========================== */}
            <div className="ct-card ct-desktopOnly">
              <div className="ct-head">
                <div className="ct-col ct-col--product">Product</div>
                <div className="ct-col ct-col--price">Price</div>
                <div className="ct-col ct-col--qty">Quantity</div>
                <div className="ct-col ct-col--sub">Subtotal</div>
              </div>

              <div className="ct-body">
                {items.length === 0 ? (
                  <div className="ct-empty">
                    <BsCartPlusFill className="icon-cart" />
                    <p className="yo-cart">Your cart is empty!</p>
                    <Link className="ct-emptyBtn" to="/product">
                      Shop Now
                    </Link>
                  </div>
                ) : (
                  items.map((item) => {
                    const lineSubtotal = (Number(item.unitPrice) || 0) * (Number(item.qty) || 0);

                    const printSideLabel =
                      item.printSide === "oneSide" ? "One-side" : item.printSide === "twoSide" ? "Two-side" : "";

                    const printDiscountApplied =
                      item.printDiscountMinQty && Number(item.qty || 1) >= Number(item.printDiscountMinQty);

                    return (
                      <div className="ct-row" key={item.cartItemId}>
                        <div className="ct-col ct-col--product">
                          <button
                            className="ct-remove"
                            type="button"
                            aria-label="Remove"
                            onClick={() => removeItem(item.cartItemId)}
                          >
                            ×
                          </button>

                          <div className="ct-prod">
                            <div
                              className="ct-img"
                              role="button"
                              tabIndex={0}
                              onClick={() => goToProduct(item.slug || item.productId)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") goToProduct(item.slug || item.productId);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <img src={item.img} alt={item.name} />
                            </div>

                            <div className="ct-prodText">
                              <div
                                className="ct-name"
                                role="button"
                                tabIndex={0}
                                onClick={() => goToProduct(item.slug || item.productId)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") goToProduct(item.slug || item.productId);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {item.name}
                              </div>

                              {(item.color || item.size) && (
                                <div className="ct-meta">
                                  {item.color ? (
                                    <>
                                      Color : <span>{item.color}</span>
                                    </>
                                  ) : null}
                                  {item.color && item.size ? " | " : null}
                                  {item.size ? (
                                    <>
                                      Size : <span>{item.size}</span>
                                    </>
                                  ) : null}
                                </div>
                              )}

                              {item.hasPrintPricing && (item.printPricingTitle || item.printSide) ? (
                                <div style={{ marginTop: 4, fontSize: 12, color: "#666", lineHeight: 1.4 }}>
                                  <div>
                                    <strong>Printing:</strong> {item.printPricingTitle || "—"}
                                    {printSideLabel ? (
                                      <>
                                        {" "}
                                        • <strong>Side:</strong> {printSideLabel}
                                      </>
                                    ) : null}
                                  </div>

                                  {item.printUnitPrice > 0 ? (
                                    <div>
                                      <strong>Print unit:</strong> {formatUGX(item.printUnitPrice)}
                                      {item.printDiscountMinQty ? (
                                        <>
                                          {" "}
                                          • <strong>Discount from:</strong> Qty ≥ {Number(item.printDiscountMinQty)}
                                          {printDiscountApplied ? <span style={{ color: "#0a7" }}> (applied)</span> : null}
                                        </>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}

                              {item.isBulkDiscount && item.discountMinQty ? (
                                <div style={{ marginTop: 4, fontSize: 12, color: "#777" }}>
                                  Bulk discount applied for qty ≥ {Number(item.discountMinQty)}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="ct-col ct-col--price">
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span>{formatUGX(item.unitPrice)}</span>
                            {item.oldUnitPrice ? (
                              <span style={{ textDecoration: "line-through", color: "#888", fontSize: 13 }}>
                                {formatUGX(item.oldUnitPrice)}
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div className="ct-col ct-col--qty">
                          <div className="ct-qty">
                            <button type="button" className="ct-qtyBtn" onClick={() => decQty(item.cartItemId, item.qty)}>
                              −
                            </button>

                            <input
                              className="ct-qtyInput"
                              value={item.qty}
                              onChange={(e) => setQty(item.cartItemId, e.target.value)}
                              inputMode="numeric"
                              aria-label="Quantity"
                            />

                            <button type="button" className="ct-qtyBtn" onClick={() => incQty(item.cartItemId, item.qty)}>
                              +
                            </button>
                          </div>
                        </div>

                        <div className="ct-col ct-col--sub">{formatUGX(lineSubtotal)}</div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="ct-bottom">
                <button className="ct-clear" type="button" onClick={clearCart} disabled={!items.length}>
                  Clear Shopping Cart
                </button>

                {setupFeeTotal > 0 ? (
                  <div style={{ marginLeft: "auto", fontSize: 13, color: "#666" }}>
                    Setup fee (once): <strong>{formatUGX(setupFeeTotal)}</strong>
                  </div>
                ) : null}
              </div>
            </div>

            {/* =========================
                MOBILE APP LIST
            ========================== */}
            <div className="ct-appList ct-mobileOnly">
              {items.length === 0 ? (
                <div className="ct-empty">
                  <BsCartPlusFill className="icon-cart" />
                  <p className="yo-cart">Your cart is empty!</p>
                  <Link className="ct-emptyBtn" to="/product">
                    Shop Now
                  </Link>
                </div>
              ) : (
                <>
                  {items.map((item) => {
                    const hasDiscount = item.oldUnitPrice && Number(item.oldUnitPrice) > Number(item.unitPrice || 0);

                    const pct = hasDiscount
                      ? Math.round(((Number(item.oldUnitPrice) - Number(item.unitPrice)) / Number(item.oldUnitPrice)) * 100)
                      : null;

                    const printSideLabel =
                      item.printSide === "oneSide" ? "One-side" : item.printSide === "twoSide" ? "Two-side" : "";

                    const printDiscountApplied =
                      item.printDiscountMinQty && Number(item.qty || 1) >= Number(item.printDiscountMinQty);

                    return (
                      <div className="ct-appItem" key={`m-${item.cartItemId}`}>
                        <div className="ct-appItemMain">
                          <div
                            className="ct-appThumb"
                            role="button"
                            tabIndex={0}
                            onClick={() => goToProduct(item.slug || item.productId)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") goToProduct(item.slug || item.productId);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <img src={item.img} alt={item.name} />
                          </div>

                          <div className="ct-appInfo">
                            <div
                              className="ct-appName"
                              role="button"
                              tabIndex={0}
                              onClick={() => goToProduct(item.slug || item.productId)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") goToProduct(item.slug || item.productId);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {item.name}
                            </div>

                            {item.color || item.size ? (
                              <div className="ct-appMeta">
                                {item.color ? <span>Color: {item.color}</span> : null}
                                {item.color && item.size ? <span className="dot">•</span> : null}
                                {item.size ? <span>Size: {item.size}</span> : null}
                              </div>
                            ) : null}

                            {item.hasPrintPricing && (item.printPricingTitle || item.printSide) ? (
                              <div style={{ marginTop: 6, fontSize: 12, color: "#666", lineHeight: 1.35 }}>
                                <div>
                                  Printing: <strong>{item.printPricingTitle || "—"}</strong>
                                  {printSideLabel ? ` • ${printSideLabel}` : ""}
                                </div>

                                {item.printUnitPrice > 0 ? (
                                  <div>
                                    Print unit: <strong>{formatUGX(item.printUnitPrice)}</strong>
                                    {item.printDiscountMinQty ? (
                                      <>
                                        {" "}
                                        • Disc from ≥ {Number(item.printDiscountMinQty)}
                                        {printDiscountApplied ? <span style={{ color: "#0a7" }}> (applied)</span> : null}
                                      </>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}

                            <div className="ct-appPriceRow">
                              <div className="ct-appPriceBlock">
                                <div className="ct-appPrice">{formatUGX(item.unitPrice)}</div>

                                <div className="ct-appOldRow">
                                  {hasDiscount ? (
                                    <span className="ct-appOld">{formatUGX(item.oldUnitPrice)}</span>
                                  ) : (
                                    <span className="ct-appOld ct-appOld--ghost" />
                                  )}

                                  {pct !== null ? <span className="ct-appOff">{`-${pct}%`}</span> : null}
                                </div>
                              </div>

                              <div className="ct-appQtyCol">
                                <div className="ct-appQty">
                                  <button
                                    type="button"
                                    className="ct-appQtyBtn"
                                    onClick={() => decQty(item.cartItemId, item.qty)}
                                  >
                                    −
                                  </button>
                                  <div className="ct-appQtyMid">{item.qty}</div>
                                  <button
                                    type="button"
                                    className="ct-appQtyBtn ct-appQtyBtn--plus"
                                    onClick={() => incQty(item.cartItemId, item.qty)}
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>

                            {item.isBulkDiscount && item.discountMinQty ? (
                              <div className="ct-appBulk">Bulk discount applied for qty ≥ {Number(item.discountMinQty)}</div>
                            ) : null}
                          </div>
                        </div>

                        <button type="button" className="ct-appRemove" onClick={() => removeItem(item.cartItemId)}>
                          Remove
                        </button>
                      </div>
                    );
                  })}

                  <div className="ct-appCheckoutBar">
                    <a className="ct-appCallBtn" href={`tel:${SUPPORT_PHONE}`} aria-label="Call">
                      <FiPhoneCall />
                    </a>

                    <button
                      type="button"
                      className="ct-appCheckoutBtn"
                      onClick={proceedToCheckout}
                      disabled={!items.length}
                    >
                      Checkout ({formatUGX(total)})
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* =========================
                DESKTOP SUMMARY
            ========================== */}
            <aside className="ct-summary ct-desktopOnly">
              <h3 className="ct-sTitle">Order Summary</h3>

              <div className="ct-sRows">
                <div className="ct-sRow">
                  <span className="ct-sKey">Items</span>
                  <span className="ct-sVal">{itemsCount}</span>
                </div>

                <div className="ct-sRow">
                  <span className="ct-sKey">Items Total</span>
                  <span className="ct-sVal">{formatUGX(itemsSubTotal)}</span>
                </div>

                {setupFeeTotal > 0 ? (
                  <div className="ct-sRow">
                    <span className="ct-sKey">Setup Fee (once)</span>
                    <span className="ct-sVal">{formatUGX(setupFeeTotal)}</span>
                  </div>
                ) : null}

                <div className="ct-sDivider" />

                <div className="ct-sRow ct-sRow--total">
                  <span className="ct-sKey">Total</span>
                  <span className="ct-sVal">{formatUGX(total)}</span>
                </div>
              </div>

              <button type="button" className="ct-checkoutBtn" onClick={proceedToCheckout} disabled={!items.length}>
                Proceed to Checkout
              </button>
            </aside>
          </div>
        </>
      )}

      {/* ✅ LOGIN PROMPT MODAL */}
      {showLoginPrompt ? (
        <div
          onClick={closeLoginPrompt}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "470px",
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
              padding: "28px 28px 22px",
              position: "relative",
            }}
          >
            <button
              type="button"
              aria-label="Close"
              onClick={closeLoginPrompt}
              style={{
                position: "absolute",
                top: "18px",
                right: "18px",
                border: "none",
                background: "transparent",
                fontSize: "28px",
                color: "#111",
                cursor: "pointer",
                lineHeight: 1,
                padding: 0,
              }}
            >
              <IoClose />
            </button>

            <h3
              style={{
                margin: 0,
                fontSize: "24px",
                lineHeight: 1.2,
                color: "#222",
                fontWeight: 700,
              }}
            >
              Please sign in to access this content
            </h3>

            <p
              style={{
                margin: "22px 0 28px",
                fontSize: "16px",
                lineHeight: 1.5,
                color: "#444",
              }}
            >
              If you are not registered just sign in with your email
            </p>

            <button type="button" className="toastsign" onClick={goToSignIn}>
              Sign In
            </button>
          </div>
        </div>
      ) : null}

      {/* RECENTLY VIEWED */}
      <section className="ct-recent">
        <div className="ct-recentShell">
          <div className="ct-recentHead">
            <div className="rec-title-wrap">
              <h2 className="ct-recentTitle">Recently Viewed Products</h2>
              <span className="rec-title-underline" />
            </div>

            <div className="ct-recentArrows">
              <button type="button" className="ct-recentArrow" onClick={() => scrollRecent("prev")} aria-label="Previous">
                ‹
              </button>
              <button type="button" className="ct-recentArrow" onClick={() => scrollRecent("next")} aria-label="Next">
                ›
              </button>
            </div>
          </div>

          <div className="na-head">
            <h3 className="na-title">Recently Viewed</h3>

            <button
              type="button"
              className="na-seeall"
              onClick={() => navigate("/product")}
              aria-label="See all products"
            >
              See all <span className="na-arrow">›</span>
            </button>
          </div>

          <div className="ct-recentTrack" ref={recentTrackRef}>
            {RECENTLY_VIEWED.length ? (
              RECENTLY_VIEWED.map((p) => {
                const priceNum = Number(p?.price ?? 0);
                const oldNum = p?.oldPrice === null || p?.oldPrice === undefined ? null : Number(p.oldPrice);

                const ratingNum = Number(p?.rating ?? p?.stars ?? p?.totalrating ?? 0);
                const ratingText = Number.isFinite(ratingNum) && ratingNum > 0 ? ratingNum.toFixed(1) : "0.0";

                const targetSlugOrId = p?.slug || p?.productSlug || p?.id;

                return (
                  <article
                    key={String(p.id)}
                    className="ct-recentCard"
                    onClick={() => goToProduct(targetSlugOrId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") goToProduct(targetSlugOrId);
                    }}
                  >
                    <div className="ct-recentImg">
                      <img src={p.img || product1} alt={p.name || "Product"} />
                    </div>

                    <div className="ct-recentBody">
                      <div className="ct-recentTopRow">
                        <h3 className="ct-recentName" title={p.name}>
                          {p.name}
                        </h3>

                        <div className="ct-recentInlineRating" aria-label={`Rating ${ratingText}`}>
                          ★({ratingText})
                        </div>
                      </div>

                      <div className="ct-recentPrices">
                        <span className="ct-recentPrice">{formatUGX(priceNum)}</span>
                        {oldNum !== null ? <span className="ct-recentOld">{formatUGX(oldNum)}</span> : null}
                      </div>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="ct-empty" style={{ padding: "18px 4px" }}>
                <p>No recently viewed products yet.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cart;